/**
 * BarmetHub Module Registry
 * Defines all modules, their groups, permissions, and routing.
 * Updated for BHM-HMS-REQ-032: new sidebar structure.
 */
import type { HubPermission } from '@barmetvault/shared';

export type ModuleStatus = 'ACTIVE' | 'COMING_SOON' | 'DISABLED' | 'ERROR';

export const moduleGroups = [
  { id: 'principal', name: 'Principal' },
  { id: 'operations', name: 'Operaciones' },
  { id: 'automation', name: 'Automatización' },
  { id: 'nexus', name: 'Nexus Core' },
  { id: 'resources', name: 'Recursos' },
  { id: 'settings', name: 'Ajustes' },
] as const;

export type ModuleGroup = typeof moduleGroups[number]['id'];

export interface ModuleDefinition {
  id: string;
  name: string;
  route: string;
  icon: string;
  permission: HubPermission | null;
  status: ModuleStatus;
  navigation: boolean;
  group: ModuleGroup;
  /** If true, only SUPERADMIN sees this module */
  superAdminOnly?: boolean;
}

// ── Module Registry ─────────────────────────────────────────────────────
export const modules: readonly ModuleDefinition[] = [
  // PRINCIPAL
  { id: 'nexus', name: 'Nexus', route: '/nexus', icon: 'bolt', permission: 'NEXUS_USE' as HubPermission, status: 'ACTIVE', navigation: true, group: 'principal' },
  { id: 'dashboard', name: 'Dashboard', route: '/dashboard', icon: 'home', permission: null, status: 'ACTIVE', navigation: true, group: 'principal' },
  { id: 'mi-trabajo', name: 'Mi trabajo', route: '/mi-trabajo', icon: 'task_alt', permission: 'TASKS_VIEW' as HubPermission, status: 'ACTIVE', navigation: true, group: 'principal' },
  { id: 'proyectos', name: 'Proyectos', route: '/proyectos', icon: 'folder_open', permission: 'PROJECTS_VIEW' as HubPermission, status: 'ACTIVE', navigation: true, group: 'principal' },

  // OPERACIONES
  { id: 'administration', name: 'Administración', route: '/operaciones/administracion', icon: 'account_balance', permission: 'holded.read' as HubPermission, status: 'COMING_SOON', navigation: true, group: 'operations' },
  { id: 'crm', name: 'CRM', route: '/operaciones/crm', icon: 'contacts', permission: 'clientify.read' as HubPermission, status: 'COMING_SOON', navigation: true, group: 'operations' },
  { id: 'marketing', name: 'Marketing', route: '/operaciones/marketing', icon: 'campaign', permission: 'marketing.read' as HubPermission, status: 'ACTIVE', navigation: true, group: 'operations' },
  { id: 'webs', name: 'Webs', route: '/operaciones/webs', icon: 'language', permission: null, status: 'COMING_SOON', navigation: true, group: 'operations' },

  // AUTOMATIZACIÓN
  { id: 'automations', name: 'Automatizaciones', route: '/automatizacion/workflows', icon: 'automation', permission: 'agents.execute' as HubPermission, status: 'COMING_SOON', navigation: true, group: 'automation' },
  { id: 'ai', name: 'IA', route: '/automatizacion/ia', icon: 'neurology', permission: 'agents.execute' as HubPermission, status: 'ACTIVE', navigation: true, group: 'automation' },

  // NEXUS CORE
  { id: 'hermes', name: 'Runtime de agentes', route: '/nexus/hermes', icon: 'hub', permission: 'agents.read' as HubPermission, status: 'ACTIVE', navigation: true, group: 'nexus', superAdminOnly: true },
  { id: 'agents', name: 'Agentes', route: '/nexus/agentes', icon: 'smart_toy', permission: 'agents.read' as HubPermission, status: 'ACTIVE', navigation: true, group: 'nexus' },
  { id: 'skills', name: 'Skills', route: '/nexus/skills', icon: 'school', permission: 'skills.read' as HubPermission, status: 'ACTIVE', navigation: true, group: 'nexus' },
  { id: 'mcp', name: 'MCP', route: '/nexus/mcp', icon: 'terminal', permission: 'integrations.read' as HubPermission, status: 'ACTIVE', navigation: true, group: 'nexus' },
  { id: 'factory-prompts', name: 'Factory Prompts', route: '/recursos/factory-prompts', icon: 'auto_awesome', permission: 'FACTORY_PROMPTS_VIEW' as HubPermission, status: 'ACTIVE', navigation: true, group: 'resources' },
  { id: 'tools', name: 'Tools', route: '/nexus/tools', icon: 'build', permission: 'agents.execute' as HubPermission, status: 'ACTIVE', navigation: true, group: 'nexus' },

  // AJUSTES (navigation handled separately in Sidebar)
  { id: 'mi-cuenta', name: 'Mi cuenta', route: '/ajustes/mi-cuenta', icon: 'person', permission: null, status: 'ACTIVE', navigation: false, group: 'settings' },
  { id: 'integraciones', name: 'Integraciones', route: '/ajustes/integraciones', icon: 'hub', permission: 'integrations.read' as HubPermission, status: 'ACTIVE', navigation: false, group: 'settings' },
  { id: 'accesos-empleados', name: 'Accesos empleados', route: '/ajustes/accesos-empleados', icon: 'badge', permission: 'EMPLOYEES_VIEW' as HubPermission, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
  { id: 'seguridad-sistema', name: 'Seguridad del sistema', route: '/ajustes/seguridad', icon: 'shield', permission: null, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
  { id: 'registro-actividad', name: 'Registro de actividad', route: '/ajustes/registro', icon: 'history', permission: null, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
  { id: 'estado-sistema', name: 'Estado del sistema', route: '/ajustes/estado', icon: 'monitor_heart', permission: null, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },

  // Legacy modules kept for routing (not in sidebar)
  { id: 'lasspass', name: 'Lasspass', route: '/recursos/lasspass', icon: 'key', permission: 'LASSPASS_VIEW' as HubPermission, status: 'ACTIVE', navigation: true, group: 'resources' },
  { id: 'users', name: 'Usuarios', route: '/sistema/usuarios', icon: 'group', permission: 'users.manage' as HubPermission, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
  { id: 'permissions', name: 'Permisos', route: '/sistema/permisos', icon: 'policy', permission: 'users.manage' as HubPermission, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
  { id: 'audit', name: 'Auditoría', route: '/sistema/auditoria', icon: 'history', permission: null, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
  { id: 'system', name: 'Estado', route: '/sistema/estado', icon: 'monitor_heart', permission: null, status: 'ACTIVE', navigation: false, group: 'settings', superAdminOnly: true },
];

// ── Legacy Redirects ────────────────────────────────────────────────────
export const legacyModuleAliases: Readonly<Record<string, string>> = {
  '/secrets': '/recursos/lasspass',
  '/lasspass': '/recursos/lasspass',
  '/prompts': '/recursos/factory-prompts',
  '/prompt-factory': '/recursos/factory-prompts',
  '/system': '/ajustes/estado',
  '/crm': '/operaciones/crm',
  '/marketing': '/operaciones/marketing',
  '/webs': '/operaciones/webs',
  '/automations': '/automatizacion/workflows',
  '/ai': '/automatizacion/ia',
  '/integrations': '/ajustes/integraciones',
  '/users': '/ajustes/usuarios',
  '/permissions': '/sistema/permisos',
  '/hub-projects': '/proyectos',
  '/security': '/ajustes/seguridad',
  '/activity': '/ajustes/registro',
  '/sistema/integraciones': '/ajustes/integraciones',
  '/sistema/usuarios': '/ajustes/usuarios',
  '/sistema/auditoria': '/ajustes/registro',
  '/sistema/estado': '/ajustes/estado',
};